// Dependencies
// -----------------------------------------------
import React from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

// -----------------------------------------------
// COMPONENT->META -------------------------------
// -----------------------------------------------
class Meta extends React.Component {
  // Constructor
  // ---------------------------------------------
  constructor(props) {
    super(props);
    this.state = { canonical: this.props.brand.canonical };
  }

  // Component Did Mount
  // ---------------------------------------------
  componentDidMount() {
    this.setCanonical();
  }

  // Set Canonical
  // ---------------------------------------------
  setCanonical = () => {
    const secure = this.state.canonical.startsWith('http');
    if (!secure) {
      this.setState({ canonical: `https://${this.state.canonical}` });
    }
  };

  setOpenGraphTitle = () => {
    let ogTitle = this.props.brand.name;
    if (
      this.props.brand.home?.homepage?.title &&
      this.props.brand.home.homepage.title !== ''
    ) {
      ogTitle = this.props.brand.home.homepage.title;
    } else if (
      this.props.brand.home?.meta_title &&
      this.props.brand.home.meta_title !== ''
    ) {
      ogTitle = this.props.brand.home.meta_title;
    }
    return ogTitle;
  };

  setOpenGraphDescription = () => {
    let ogDescription = this.props.brand.name;
    if (
      this.props.brand.home?.homepage?.description &&
      this.props.brand.home.homepage.description !== ''
    ) {
      ogDescription = this.props.brand.home.homepage.description;
    } else if (
      this.props.brand.home?.meta_description &&
      this.props.brand.home.meta_description !== ''
    ) {
      ogDescription = this.props.brand.home.meta_description;
    }
    return ogDescription;
  };

  contactPhone = () => {
    const { contact } = this.props.brand;
    if (!contact) return '';
    return (
      contact.phone_primary?.number ||
      contact.phone ||
      ''
    );
  };

  logoImageUrl = () => {
    const { logo_image: logoImage } = this.props.brand;
    if (!logoImage) return '';
    if (typeof logoImage === 'string') return logoImage;
    return logoImage.url || '';
  };

  homeLocation = () => this.props.brand.home?.location || {};

  // Render
  // ---------------------------------------------
  render() {
    const location = this.homeLocation();
    const phone = this.contactPhone();
    const logoUrl = this.logoImageUrl();
    const metaTitle =
      this.props.brand.home?.meta_title ||
      this.props.brand.home?.homepage?.title ||
      this.props.brand.name;
    const metaDescription =
      this.props.brand.home?.meta_description ||
      this.props.brand.home?.homepage?.description ||
      this.props.brand.name;

    return (
      <Helmet>
        <title>{metaTitle}</title>
        <link rel="canonical" href={this.state.canonical} />
        <meta name="description" content={metaDescription} />
        <meta itemProp="name" content={this.props.brand.name} />
        <meta itemProp="description" content={metaDescription} />
        <meta itemProp="image" content="https://example.com/image.jpg" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:site"
          content={this.props.brand.brand_info?.social?.social_twitter || ''}
        />
        <meta name="twitter:url" content={this.props.brand.canonical} />
        <meta name="twitter:title" content={this.setOpenGraphTitle()} />
        <meta
          name="twitter:description"
          content={this.setOpenGraphDescription()}
        />
        <meta name="twitter:image" content="https://example.com/image.jpg" />
        {/* Facebook */}
        <meta property="og:image" content="http://example.com/image.jpg" />
        <meta property="og:site_name" content={this.props.brand.name} />
        <meta property="og:title" content={this.setOpenGraphTitle()} />
        <meta
          property="og:description"
          content={this.setOpenGraphDescription()}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={this.props.brand.canonical} />
        <meta property="og:locale" content="en_US" />
        {location.adr_street && (
          <meta property="og:street-address" content={location.adr_street} />
        )}
        {location.adr_city && (
          <meta property="og:locality" content={location.adr_city} />
        )}
        {location.adr_state && (
          <meta property="og:region" content={location.adr_state} />
        )}
        {location.adr_postal_code && (
          <meta property="og:postal-code" content={location.adr_postal_code} />
        )}
        {location.adr_country && (
          <meta property="og:country-name" content={location.adr_country} />
        )}
        {this.props.brand.contact?.email && (
          <meta property="og:email" content={this.props.brand.contact.email} />
        )}
        {phone && <meta property="og:phone_number" content={phone} />}
        {/* JSON/LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LodgingBusiness',
            name: this.props.brand.name,
            url: this.state.canonical,
            ...(phone ? { telephone: phone } : {}),
            ...(logoUrl ? { image: logoUrl } : {}),
            ...(location.adr_street
              ? {
                  address: {
                    '@type': 'PostalAddress',
                    streetAddress: location.adr_street,
                    addressLocality: location.adr_city || '',
                    addressRegion: location.adr_state || '',
                    postalCode: location.adr_postal_code || '',
                    addressCountry: location.adr_country || ''
                  }
                }
              : {})
          })}
        </script>
      </Helmet>
    );
  }
}

// Map State to Props
// -----------------------------------------------
function mapStateToProps(state) {
  return {
    brand: state.brand ? state.brand : {}
  };
}

// Export
// -----------------------------------------------
export default connect(mapStateToProps)(Meta);
